// Deterministic SIMAP classification tags for tenders.
//
// The backend serves Tags[] per tender (simap_core.tender_tags_v via the
// tender Lambda): 'type:<order_type>', 'proc:<process_type>', 'canton:<abbr>',
// 'cpv:<2-digit division>' and the marker 'construction-codes' (the tender
// carries BKP/eBKP/NPK positions). This module owns everything non-visual:
// parsing, labels, row badge selection, facet counting and facet filtering.

export type TagKind = 'type' | 'proc' | 'canton' | 'cpv' | 'construction' | 'topic' | 'subtopic' | 'code' | 'docs' | 'other';

// 'docs:nda': only a non-disclosure agreement is published for the tender (the
// documents are still behind it). 'docs:nda-required': the documents are in,
// but an NDA had to be signed to get them — every bidder has to sign.
export const NDA_TAG = 'docs:nda';
export const NDA_REQUIRED_TAG = 'docs:nda-required';
const DOCS_LABELS: Record<string, string> = {
    nda: 'Documents behind NDA',
    'nda-required': 'NDA required',
};

export interface TagInfo {
    kind: TagKind;
    value: string;
    label: string;
}

// Which values are selected in each facet chip group. Within a facet the
// values are ORed, across facets ANDed — standard facet semantics.
export interface TagSelection {
    type: string[];
    industry: string[]; // cpv divisions
    canton: string[];
    topic: string[];    // topic slugs, or NO_TOPIC
    subtopic: string[]; // sub-topic slugs or code tags ('bkp:221'), within the single selected topic
}

export const emptyTagSelection: TagSelection = { type: [], industry: [], canton: [], topic: [], subtopic: [] };
export const NO_TOPIC = '__none__';

// The active taxonomy from GET /topics: names for the slugs the tags carry
// ('topic:<slug>', 'subtopic:<slug>') and labels for the BKP/eBKP sub-level
// codes ('bkp:221'). Handed in by the page; until it arrives, slugs are shown.
export interface TopicInfo {
    slug: string;
    name: string;
    group: string;
    level: 1 | 2;
    parent: string | null;
    sort: number;
}

export interface TopicCatalog {
    version: number | null;
    topics: TopicInfo[];
    codes: Record<string, string>;
}

export const emptyTopicCatalog: TopicCatalog = { version: null, topics: [], codes: {} };

export const GROUP_LABELS: Record<string, string> = {
    buildings: 'Buildings', civil: 'Civil works', planning: 'Planning', it: 'IT',
    'public-services': 'Public services', supplies: 'Supplies',
};
const GROUP_ORDER = Object.keys(GROUP_LABELS);

let catalog: TopicCatalog = emptyTopicCatalog;
let topicIndex = new Map<string, TopicInfo>();
// parseTag runs for every tag of every tender on every facet computation; the
// tag vocabulary is a few hundred strings, so results are cached until the
// catalog changes (a new catalog object — the page hands in the same object
// on every render).
const parseCache = new Map<string, TagInfo>();
export const setTopicCatalog = (next: TopicCatalog | undefined): void => {
    const resolved = next ?? emptyTopicCatalog;
    if (resolved === catalog) return;
    catalog = resolved;
    topicIndex = new Map(catalog.topics.map(t => [t.slug, t]));
    parseCache.clear();
};
const topicInfo = (slug: string): TopicInfo | undefined => topicIndex.get(slug);
const codeLabel = (tag: string): string => {
    const [scheme, code] = tag.split(':');
    const prefix = scheme === 'ebkp' ? 'eBKP' : scheme.toUpperCase();
    const label = catalog.codes[tag];
    return label ? `${prefix} ${code} · ${label}` : `${prefix} ${code}`;
};

const TYPE_LABELS: Record<string, string> = {
    construction: 'Construction',
    services: 'Services',
    supplies: 'Supplies',
};

const PROC_LABELS: Record<string, string> = {
    invitation: 'By invitation',
    direct: 'Direct award',
};

export const CANTON_NAMES: Record<string, string> = {
    AG: 'Aargau', AI: 'Appenzell Innerrhoden', AR: 'Appenzell Ausserrhoden',
    BE: 'Bern', BL: 'Basel-Landschaft', BS: 'Basel-Stadt', FR: 'Fribourg',
    GE: 'Geneva', GL: 'Glarus', GR: 'Graubünden', JU: 'Jura', LU: 'Lucerne',
    NE: 'Neuchâtel', NW: 'Nidwalden', OW: 'Obwalden', SG: 'St. Gallen',
    SH: 'Schaffhausen', SO: 'Solothurn', SZ: 'Schwyz', TG: 'Thurgau',
    TI: 'Ticino', UR: 'Uri', VD: 'Vaud', VS: 'Valais', ZG: 'Zug', ZH: 'Zurich',
};

// Standard CPV division (first two digits) labels, shortened for chips.
export const CPV_DIVISION_LABELS: Record<string, string> = {
    '03': 'Agriculture & farming', '09': 'Fuels & energy', '14': 'Mining & raw materials',
    '15': 'Food & beverages', '16': 'Agricultural machinery', '18': 'Clothing & footwear',
    '19': 'Leather & textiles', '22': 'Printed matter', '24': 'Chemical products',
    '30': 'Office & computer equipment', '31': 'Electrical equipment',
    '32': 'Radio & telecom equipment', '33': 'Medical equipment & pharma',
    '34': 'Transport equipment', '35': 'Security & defence', '37': 'Sports & musical goods',
    '38': 'Lab & precision equipment', '39': 'Furniture & furnishings', '41': 'Water',
    '42': 'Industrial machinery', '43': 'Construction machinery', '44': 'Construction materials',
    '45': 'Construction work', '48': 'Software & IT systems', '50': 'Repair & maintenance',
    '51': 'Installation services', '55': 'Hotel & catering', '60': 'Transport services',
    '63': 'Transport support services', '64': 'Post & telecom services', '65': 'Public utilities',
    '66': 'Financial & insurance', '70': 'Real estate services', '71': 'Architecture & engineering',
    '72': 'IT services', '73': 'Research & development', '75': 'Public administration',
    '76': 'Oil & gas services', '77': 'Agriculture & forestry services', '79': 'Business services',
    '80': 'Education & training', '85': 'Health & social work', '90': 'Environmental services',
    '92': 'Culture & sport', '98': 'Other community services',
};

const capitalize = (value: string): string => {
    const text = value.replace(/[_-]+/g, ' ').trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : value;
};

export const parseTag = (tag: string): TagInfo => {
    const cached = parseCache.get(tag);
    if (cached) return cached;
    const info = parseTagUncached(tag);
    parseCache.set(tag, info);
    return info;
};

const parseTagUncached = (tag: string): TagInfo => {
    if (tag === 'construction-codes') {
        return { kind: 'construction', value: tag, label: 'Construction codes' };
    }
    const sep = tag.indexOf(':');
    if (sep > 0) {
        const prefix = tag.slice(0, sep);
        const value = tag.slice(sep + 1);
        switch (prefix) {
            case 'type':
                return { kind: 'type', value, label: TYPE_LABELS[value] ?? capitalize(value) };
            case 'proc':
                return { kind: 'proc', value, label: PROC_LABELS[value] ?? capitalize(value) };
            case 'canton':
                return { kind: 'canton', value, label: CANTON_NAMES[value] ?? value };
            case 'cpv':
                return { kind: 'cpv', value, label: CPV_DIVISION_LABELS[value] ?? `CPV ${value}` };
            case 'topic':
                return { kind: 'topic', value, label: topicInfo(value)?.name ?? capitalize(value) };
            case 'subtopic':
                return { kind: 'subtopic', value, label: topicInfo(value)?.name ?? capitalize(value) };
            case 'bkp':
            case 'ebkp':
                return { kind: 'code', value: tag, label: codeLabel(tag) };
            case 'docs':
                return { kind: 'docs', value, label: DOCS_LABELS[value] ?? capitalize(value) };
            default:
                break;
        }
    }
    return { kind: 'other', value: tag, label: capitalize(tag) };
};

// The few chips worth showing on a list row: what it is (type), what it is
// about (topic), the industries (CPV divisions), where (canton). Process
// type, sub-topics, codes and the construction-codes marker stay out of the
// rows — they earn their space in the details panel, not on every line.
export const rowBadges = (tags: string[] | undefined, max = 4): TagInfo[] => {
    const parsed = (tags ?? []).map(parseTag);
    const order: TagKind[] = ['type', 'cpv', 'topic', 'canton'];
    return order
        .flatMap(kind => parsed.filter(t => t.kind === kind))
        .slice(0, max);
};

// Every tag for the details panel, ordered by what a reader wants first:
// what it is, what it is about (topic, sub-topic or codes), which
// industries, where, how it is procured, then markers.
export const detailBadges = (tags: string[] | undefined): TagInfo[] => {
    const parsed = (tags ?? []).map(parseTag);
    const order: TagKind[] = ['type', 'cpv', 'topic', 'subtopic', 'code', 'canton', 'proc', 'construction', 'docs', 'other'];
    return order.flatMap(kind => parsed.filter(t => t.kind === kind));
};

export type FacetKey = 'type' | 'industry' | 'topic' | 'canton' | 'subtopic';

// The tenders a facet's counts are taken from: every OTHER facet of the
// (effective) selection applied, the facet's own selection cleared — the
// standard faceted-search rule. A dropdown's numbers never change because you
// clicked inside it, but they follow the other dropdowns, so "Roads (400)"
// means 400 you will actually see. Clearing the topic also clears the
// sub-topic (it only exists within one topic).
export const scopedTags = (
    tagsPerTender: (string[] | undefined)[],
    selection: TagSelection,
    facet: FacetKey,
): (string[] | undefined)[] => {
    const others: TagSelection = { ...selection, [facet]: [] };
    if (facet === 'topic') others.subtopic = [];
    return tagsPerTender.filter(tags => matchesTagSelection(tags, others));
};

export interface FacetCounts {
    type: Record<string, number>;
    industry: Record<string, number>;
    canton: Record<string, number>;
    topic: Record<string, number>; // topic slugs, plus NO_TOPIC for tenders without one
}

export const facetCounts = (tagsPerTender: (string[] | undefined)[]): FacetCounts => {
    const counts: FacetCounts = { type: {}, industry: {}, canton: {}, topic: {} };
    const bump = (facet: Record<string, number>, value: string) => {
        facet[value] = (facet[value] ?? 0) + 1;
    };
    for (const tags of tagsPerTender) {
        let hasTopic = false;
        for (const tag of tags ?? []) {
            const { kind, value } = parseTag(tag);
            if (kind === 'type') bump(counts.type, value);
            else if (kind === 'cpv') bump(counts.industry, value);
            else if (kind === 'canton') bump(counts.canton, value);
            else if (kind === 'topic') { bump(counts.topic, value); hasTopic = true; }
        }
        if (!hasTopic) bump(counts.topic, NO_TOPIC);
    }
    return counts;
};

export interface FacetOption {
    value: string;
    label: string;
    count: number;
    group?: string;
}

export interface TopicTreeNode extends FacetOption {
    children: FacetOption[]; // sub-topics (catalog order) then BKP/eBKP codes (by count)
}

// One tree for the Topic menu, in ONE pass over the tenders: level-1 topics
// in catalog order (group, then sort), only topics present, each with the
// sub-topics or codes the tenders of that topic carry, then the Other item
// for tenders without a topic. Topics the catalog does not know (not loaded
// yet) stay usable under their slug. Counts are within the tags handed in
// (scope them by the other facets first).
export const topicTree = (tagsPerTender: (string[] | undefined)[]): TopicTreeNode[] => {
    const counts: Record<string, number> = {};
    const childCounts: Record<string, Record<string, number>> = {};
    let untagged = 0;
    for (const tags of tagsPerTender) {
        const parsed = (tags ?? []).map(parseTag);
        const topic = parsed.find(t => t.kind === 'topic')?.value;
        if (!topic) {
            untagged += 1;
            continue;
        }
        counts[topic] = (counts[topic] ?? 0) + 1;
        const bucket = childCounts[topic] ?? (childCounts[topic] = {});
        for (const t of parsed) {
            if (t.kind === 'subtopic' || t.kind === 'code') bucket[t.value] = (bucket[t.value] ?? 0) + 1;
        }
    }
    // Named sub-topics first in catalog order, then codes by count.
    const rank = (value: string): number => {
        const info = topicInfo(value);
        return info && info.level === 2 ? info.sort : Number.MAX_SAFE_INTEGER;
    };
    const children = (topic: string): FacetOption[] => Object.entries(childCounts[topic] ?? {})
        .sort((a, b) => rank(a[0]) - rank(b[0]) || b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([value, count]) => ({
            value, count, label: parseTag(value.includes(':') ? value : `subtopic:${value}`).label,
        }));
    const present = catalog.topics
        .filter(t => t.level === 1 && counts[t.slug])
        .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group) || a.sort - b.sort);
    const nodes: TopicTreeNode[] = present.map(t => ({
        value: t.slug, label: t.name, group: GROUP_LABELS[t.group] ?? t.group, count: counts[t.slug],
        children: children(t.slug),
    }));
    Object.keys(counts)
        .filter(v => !present.some(t => t.slug === v))
        .sort()
        .forEach(v => nodes.push({ value: v, label: capitalize(v), group: 'Other', count: counts[v], children: children(v) }));
    if (untagged) {
        nodes.push({ value: NO_TOPIC, label: 'Other / no topic', group: 'Other', count: untagged, children: [] });
    }
    return nodes;
};

// Topic items without the hierarchy.
export const topicOptions = (tagsPerTender: (string[] | undefined)[]): FacetOption[] =>
    topicTree(tagsPerTender).map(({ children, ...option }) => option);

// The children of ONE topic: its sub-topic tags, or for a building topic its
// BKP/eBKP codes — whatever the tenders of that topic carry.
export const subtopicOptions = (tagsPerTender: (string[] | undefined)[], topic: string): FacetOption[] =>
    topicTree(tagsPerTender).find(t => t.value === topic)?.children ?? [];

// The company profile's RelevantIndustries setting defaults the industry
// facet while the "My industries" toggle is on, and RelevantTopics defaults
// the topic facet while "My topics" is on; explicit clicks in a facet win
// over its default.
export const effectiveSelection = (
    selection: TagSelection,
    defaultIndustries: string[] | undefined,
    useDefaults: boolean,
    defaultTopics: string[] | undefined = [],
    useTopicDefaults = false,
): TagSelection => {
    let out = selection;
    const industries = defaultIndustries ?? [];
    if (useDefaults && selection.industry.length === 0 && industries.length > 0) {
        out = { ...out, industry: industries };
    }
    const topics = defaultTopics ?? [];
    if (useTopicDefaults && selection.topic.length === 0 && selection.subtopic.length === 0 && topics.length > 0) {
        out = { ...out, topic: topics };
    }
    return out;
};

// "Hide low fit" floor: scored-below-floor tenders are dropped, but unscored
// tenders (usually brand new) and pursued ones (the human decision outranks
// the score) always stay visible. Scores arrive 0-1 from the API but may
// already be on the 0-100 scale.
export const passesFitFloor = (
    score: number | null | undefined,
    decision: 'pursue' | 'dismiss' | null | undefined,
    hideLowFit: boolean,
    floorPct = 40,
): boolean => {
    if (!hideLowFit) return true;
    if (score == null) return true;
    if (decision === 'pursue') return true;
    const pct = score <= 1 ? score * 100 : score;
    return pct >= floorPct;
};

// List ordering. In every sort the human decision outranks everything:
// pursued pinned on top, dismissed at the bottom (when shown).
// 'fit': highest score first, unscored last. 'deadline': soonest submission
// deadline first, missing deadlines last. 'newest': latest publication
// first, missing dates last. Fit breaks ties everywhere.
export type TenderSort = 'fit' | 'deadline' | 'newest';

export interface SortableTender {
    decision?: 'pursue' | 'dismiss' | null;
    matchingScore?: number | null;
    tenderStart?: string | null;
    publicationDate?: string | null;
    name: string;
}

export const tenderComparator = (sortBy: TenderSort) =>
    (a: SortableTender, b: SortableTender): number => {
        const rank = (t: SortableTender) =>
            t.decision === 'pursue' ? 2 : t.decision === 'dismiss' ? 0 : 1;
        if (rank(b) !== rank(a)) return rank(b) - rank(a);
        const fit = (t: SortableTender) => t.matchingScore ?? -1;
        if (sortBy === 'deadline') {
            const when = (t: SortableTender) => {
                const ms = Date.parse(t.tenderStart ?? '');
                return isNaN(ms) ? Infinity : ms;
            };
            if (when(a) !== when(b)) return when(a) - when(b);
        }
        if (sortBy === 'newest') {
            const pub = (t: SortableTender) => {
                const ms = Date.parse(t.publicationDate ?? '');
                return isNaN(ms) ? -Infinity : ms; // missing dates sink in a desc sort
            };
            if (pub(a) !== pub(b)) return pub(b) - pub(a);
        }
        if (fit(b) !== fit(a)) return fit(b) - fit(a);
        return a.name.localeCompare(b.name);
    };

export const matchesTagSelection = (
    tags: string[] | undefined,
    selection: TagSelection,
): boolean => {
    const parsed = (tags ?? []).map(parseTag);
    const has = (kind: TagKind, values: string[]): boolean => {
        if (values.length === 0) return true; // facet not active
        const own = parsed.filter(t => t.kind === kind).map(t => t.value);
        return values.some(v => own.includes(v));
    };
    // Topic is one hierarchical facet: a tender passes when its topic is
    // selected as a whole OR one of its sub-topics / codes is selected.
    // NO_TOPIC means "tenders without any topic tag".
    const wantsTopic = selection.topic.length > 0 || selection.subtopic.length > 0;
    const topicMatch = !wantsTopic
        || selection.topic.some(v =>
            v === NO_TOPIC ? !parsed.some(t => t.kind === 'topic')
                : parsed.some(t => t.kind === 'topic' && t.value === v))
        || selection.subtopic.some(v =>
            parsed.some(t => (t.kind === 'subtopic' || t.kind === 'code') && t.value === v));
    return has('type', selection.type)
        && has('cpv', selection.industry)
        && has('canton', selection.canton)
        && topicMatch;
};
